import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import AccessibilityWidget from "../../../components/AccessibilityWidget";
import { DizeeInput2 } from '../../../components/DizeeComponent/DixeeInput2';
import { NextButton } from '../../../components/DizeeComponent/NextButton';
import { postDataAPI } from '../../../utils/fetchData';
import { clearAlerts, setLoader, setSuccessAlert, setErrorAlert } from '../../../store/alert/alertSlice';
import TypeWriter from '../../../components/OnBoard/Typewriter';
import Spotify from '../../../svg/Social/Spotify';
import dizeeLogo from '../../../assets/images/dizee logo.svg';
import { searchBandsintownForArtist, importBandsintownBookings } from '../../../utils/bandsintownAgent';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 350px;
  margin: 0 auto;
  padding-left: 35px;
  padding-top: 0;
  overflow: hidden;
  height: 100vh;
  position: relative;
  touch-action: none;
`;

const FormSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-bottom: 80px;
  gap: 2px;
  margin-top: -40px;
  height: 100%;
  position: relative;
  padding-top: 40px;
`;

const ButtonContainer = styled.div`
  height: 40px;
  margin-top: 40px;
  display: flex;
  align-items: center;
  position: relative;
`;

const Footer = styled.div`
  position: fixed;
  bottom: 27px;
  width: 280px;
`;

const LogoImage = styled.img`
  width: 48px;
  height: auto;
  margin-bottom: 16px;
`;

const FooterText = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  max-width: 280px;
  line-height: 1.5;
`;

const ResultsContainer = styled.div`
  margin-top: 20px;
  max-height: 200px;
  overflow-y: auto;
  scrollbar-width: none;
  
  &::-webkit-scrollbar {
    display: none;
  }
`;

const ResultItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  font-size: 12px;
  color: white;
`;

const LoadingSpinner = styled.div`
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  border-top: 2px solid white;
  width: 16px;
  height: 16px;
  animation: spin 1s linear infinite;
  margin-right: 8px;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Add this styled component for consistent text sizing
const DizeeText = styled.span`
  font-size: 12px;
  color: white;
`;

const Step4AI = ({ setStep, setUser, user }) => {
  const [spotifyLink, setSpotifyLink] = useState("");
  const [isValidLink, setIsValidLink] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [discoveredLinks, setDiscoveredLinks] = useState(null);
  const [profileList, setList] = useState(null);
  const [linksFound, setLinksFound] = useState(0);
  const [artistName, setArtistName] = useState("");
  const dispatch = useDispatch();

  // Control page scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.height = '100%';
    document.body.style.touchAction = 'none';

    return () => {
      document.body.style.overflow = 'unset';
      document.documentElement.style.overflow = 'unset';
      document.body.style.position = 'static';
      document.body.style.width = 'auto';
      document.body.style.height = 'auto';
      document.body.style.touchAction = 'auto';
    };
  }, []);

  // Validate Spotify link
  const validateSpotifyLink = (link) => {
    if (!link) return false;
    try {
      const url = new URL(link);
      return url.hostname.includes('spotify.com');
    } catch {
      return false;
    }
  };

  // Handle link input change
  const handleLinkChange = async (e) => {
    const value = e.target.value;
    // Format with https:// if not present
    // const formattedValue = value.startsWith('http') ? value : `https://${value}`;
    setSpotifyLink(value);
    setIsValidLink(validateSpotifyLink(value));

    if (!validateSpotifyLink(value)) {
      const response = await postDataAPI("user/discoverLinksFromArtist", { artistName: value });
      console.log(`response.data.response::`, response.data)
      if (response.data) {
        setList(response.data.data.response);
      }
    }
  };

  // Process with AI to discover links
  const discoverLinks = async () => {
    if (!isValidLink) return;

    setIsProcessing(true);
    dispatch(clearAlerts());
    dispatch(setLoader(true));

    try {
      console.log("Sending request to discover links for:", spotifyLink);
      const response = await postDataAPI("user/discoverLinksWithAI", { spotifyUrl: spotifyLink });

      console.log("Received response:", response.data);

      if (response.data?.status === 200) {
        const links = response.data?.data?.response;
        console.log("Discovered links:", links);

        // Save all links to state
        setDiscoveredLinks({
          spotify: links?.spotify || "",
          instagram: links?.instagram || "",
          tiktok: links?.tiktok || "",
          twitter: links?.twitter || "",
          facebook: links?.facebook || "",
          youtube: links?.youtube || "",
          soundcloud: links?.soundcloud || "",
          appleMusic: links?.appleMusic || "",
          topSongs: links?.topSongs || [],
          topVideo: links?.topVideo || "",
          events: links?.events || []
        });

        // Calculate number of links found
        const validLinkCount = Object.entries(links)
          .filter(([key, value]) =>
            key !== 'artistName' &&
            key !== 'topSongs' &&
            key !== 'events' &&
            key !== 'profileImage' &&
            value &&
            (typeof value === 'string' ? value.trim() !== '' : value.length > 0)
          ).length;

        setLinksFound(validLinkCount);
        setArtistName(links?.artistName || "your artist");

        // Update user data with discovered links
        const updatedUser = {
          ...user,
          name: user.name || links?.artistName,
          avatar: links?.profileImage || user.avatar,
          topSongs: links?.topSongs || [],
          topVideo: links?.topVideo || {},
          social: {
            ...user?.social,
            sf: links?.spotify || user?.social?.sf || "",
            ig: links?.instagram || user?.social?.ig || "",
            tt: links?.tiktok || user?.social?.tt || "",
            x: links?.twitter || user?.social?.x || "",
            fb: links?.facebook || user?.social?.fb || "",
            yt: links?.youtube || user?.social?.yt || "",
            sc: links?.soundcloud || user?.social?.sc || "",
            am: links?.appleMusic || user?.social?.am || "",
          },
        };

        setUser(updatedUser);

        // Show success message
        dispatch(setSuccessAlert(`Found links for ${links?.artistName || "your artist"}`));

        // Note: Auto-population will happen automatically during account creation
        console.log('🎯 Discover Links completed successfully');
        console.log('🎯 Artist:', links?.artistName);
        console.log('🎯 Spotify URL:', links?.spotify || spotifyLink);
        console.log('🎯 Links found:', validLinkCount);
        console.log('🎯 User data updated with social links:', updatedUser.social);
        console.log('🎯 Auto-population will be triggered automatically when account is created');

        // Auto-import Bandsintown shows if artist name is available
        if (links?.artistName) {
          // Store artist name for post-signup import
          console.log('💾 Storing artist name for Bandsintown import after account creation:', links.artistName);
          localStorage.setItem('pendingBandsintownImport', links.artistName);
          
          // Also add to user object so it's available immediately after signup
          setUser(prevUser => ({
            ...prevUser,
            ...updatedUser,
            pendingBandsintownImport: links.artistName
          }));
        }

      } else {
        dispatch(setErrorAlert(response.data?.message || "Couldn't find related links. Please try a different Spotify URL."));
      }
    } catch (error) {
      console.error("Error discovering links:", error);
      dispatch(setErrorAlert(error?.response?.data?.message || "Error connecting to service. Please try again."));
    } finally {
      setIsProcessing(false);
      dispatch(setLoader(false));
    }
  };



  const discoverLinks1 = async () => {


    setIsProcessing(true);
    dispatch(clearAlerts());
    dispatch(setLoader(true));

    const response = await postDataAPI("user/discoverLinksFromArtist", { artistName: spotifyLink });

    // try {
    //   console.log("Sending request to discover links for:", spotifyLink);
    //   const response = await postDataAPI("user/discoverLinksFromArtist", { artistName: spotifyLink });

    // } catch (error) {
    //   console.error("Error discovering links:", error);
    //   dispatch(setErrorAlert(error?.response?.data?.message || "Error connecting to service. Please try again."));
    // } finally {
    //   setIsProcessing(false);
    //   dispatch(setLoader(false));
    // }
  };

  const getFooterText = () => {
    if (!spotifyLink) {
      return "Enter your Spotify artist link to discover your profiles and connect your online presence.";
    }

    if (!isValidLink) {
      return "Please enter a valid Spotify artist link (e.g., https://open.spotify.com/artist/...)";
    }

    if (isProcessing) {
      return "Our AI is finding all your connected profiles and content...";
    }

    if (discoveredLinks) {
      return "Perfect! We've found these links connected to your Spotify profile. Click Next to continue.";
    }

    return "Click 'Discover' to find your connected profiles and set up your DIZEE experience.";
  };

  const getPlatformLabel = (platform) => {
    const labels = {
      spotify: "Spotify",
      instagram: "Instagram",
      tiktok: "TikTok",
      twitter: "Twitter/X",
      facebook: "Facebook",
      youtube: "YouTube",
      soundcloud: "SoundCloud",
      appleMusic: "Apple Music",
      topVideo: "Top Video"
    };

    return labels[platform] || platform;
  };

  return (
    <Container>
      <FormSection>
        <TypeWriter text={"Let's find your online presence"} speed={45} />
        <div style={{ marginTop: '2px' }} className="relative">
          <div className="flex items-center gap-x-[10px] mb-4">
            <Spotify className="h-[15px] w-[15px]" />
            <DizeeInput2
              placeholder={"Enter your Spotify artist link"}
              value={spotifyLink}
              onChange={handleLinkChange}
              className={"dizee-input w-[200px]"}
            />
          </div>
          {
            console.log(`profileList--`, profileList)
          }

          {
            profileList &&
            <div className="absolute z-10 overflow-hidden rounded-[4px] shadow mt-1 w-[200px]">
              {
                profileList.length > 0 && <div className="h-2 bg-[#111111]"></div>
              }
              <div className="top-full w-full max-h-[200px] overflow-y-auto bg-[#111111] px-[5px] flex flex-col gap-y-[10px]">
                {
                  profileList.length == 0 &&
                  <span className="text-[12px]">0 result found</span>
                }
                {
                  profileList.length > 0 && profileList.map((profile, key) => (
                    <div onClick={() => {
                      setSpotifyLink(profile.spotifyUrl); setIsValidLink(validateSpotifyLink(profile.spotifyUrl)); setList(null);
                    }} className="flex flex-row items-center gap-x-[13px] cursor-pointer">
                      <img src={profile.image} className="w-[26px] h-[26px] rounded-full" />
                      <span className="text-[12px]">{profile.name}</span>
                    </div>
                  ))
                }
              </div>
              {
                profileList.length > 0 && <div className="h-2 bg-[#111111]"></div>
              }
            </div>
          }

          {/* Left-justified Discover button */}
          {isValidLink && !isProcessing && !discoveredLinks && (
            <div className="mt-2">
              <NextButton
                onClick={discoverLinks}
                className="cursor-pointer"
              >
                Discover
              </NextButton>
            </div>
          )}
        </div>

        {/* Left-justified loading state with consistent font size */}
        {isProcessing && (
          <div className="flex flex-col mt-6 mb-4">
            <div className="flex items-center">
              <LoadingSpinner className="w-[16px] h-[16px]" />
              <DizeeText className="ml-2">Discovering your links...</DizeeText>
            </div>
            <DizeeText style={{ fontSize: '10px', opacity: 0.7 }} className="mt-2">
              This may take a moment
            </DizeeText>
          </div>
        )}

        {/* Left-justified success message with consistent font size */}
        {discoveredLinks && !isProcessing && (
          <div className="flex flex-col mt-6 mb-4">
            <div className="mt-2">
              <DizeeText>
                Found <span className="font-bold">{linksFound}</span> links for {artistName}
              </DizeeText>
              {discoveredLinks.topSongs && discoveredLinks.topSongs.length > 0 && (
                <DizeeText style={{ fontSize: '10px', opacity: 0.7 }} className="block mt-1">
                  Including {discoveredLinks.topSongs.length} top songs
                </DizeeText>
              )}
            </div>
          </div>
        )}

        <ButtonContainer>
          {discoveredLinks ? (
            <NextButton
              className="cursor-pointer mr-[22px]"
              onClick={() => {
                // Skip color selection step and auto-select Classic colors
                setUser({ ...user, background: "121212", accent: "FFFFFF" });
                setStep(22);
              }}
            >
              Next
            </NextButton>
          ) : (
            <div className="flex items-center mr-[22px] opacity-50">
              <span className="text-[12px] text-white">Next</span>
            </div>
          )}
          <NextButton
            className="cursor-pointer"
            onClick={() => setStep(4)}
          >
            Go back
          </NextButton>
        </ButtonContainer>
      </FormSection>

      <Footer>
        <LogoImage src={dizeeLogo} alt="DIZEE" />
        <FooterText>
          {getFooterText()}
        </FooterText>
      </Footer>
      <AccessibilityWidget />
    </Container>
  );
};

export default Step4AI; 